"use client";

import { EASE_OUT_QUART } from "@/lib/animation-constants";
import { AnimatePresence, motion } from "motion/react";
import { Fragment, useEffect, useRef, useState } from "react";

import Link from "next/link";
import UnicornScene from "unicornstudio-react/next";

const tabs = [
  {
    id: "businesses",
    label: "Businesses",
    icon: "/images/homepage/tab-icon-businesses.svg",
    tagLabel: "For Businesses",
    headline: "Automate Operations On-Prem",
    subtitle: "Save time & resources setting blockchain tasks on autopilot",
    video: { src: "https://cdn.b3.fun/b3os-video.mp4", type: "video/mp4" },
    badges: [
      "Log Shopify Payments",
      "Payment Approvals",
      "Auto Pay Contractors",
      "Approve Invoices in Slack",
      "Create Payment Link",
    ],
  },
  {
    id: "builders",
    label: "Developers",
    icon: "/images/homepage/tab-icon-developers.svg",
    tagLabel: "For Developers",
    headline: "Innovate Onchain",
    subtitle: "Vibe code reliable smart contracts — in seconds.",
    video: { src: "https://cdn.b3.fun/developers.webm", type: "video/webm" },
    badges: ["Deploy Contracts", "Generate ABIs", "Test Workflows", "CI/CD Pipelines"],
  },
  {
    id: "traders",
    label: "Traders",
    icon: "/images/homepage/tab-icon-traders.svg",
    tagLabel: "For Traders",
    headline: "Trade Smarter",
    subtitle: "Run custom or prebuilt automations that give you an edge.",
    video: { src: "https://cdn.b3.fun/builder.webm", type: "video/webm" },
    badges: ["Copytrading", "Portfolio Rebalance", "Stop Loss Automation", "Price Alerts"],
  },
] as const;

export function AutomateOperations() {
  const [activeTab, setActiveTab] = useState<string>("businesses");
  const currentTab = tabs.find(t => t.id === activeTab) ?? tabs[0];
  const contentRef = useRef<HTMLDivElement>(null);
  const [sceneVisible, setSceneVisible] = useState(false);
  const [sceneError, setSceneError] = useState(false);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSceneVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="automate" className="px-6 py-10">
      <div className="mx-auto max-w-[1240px]">
        {/* White card container */}
        <div className="border-ds-neutral-200 overflow-hidden rounded-xl border bg-white">
          {/* Tab Header */}
          <div className="border-ds-neutral-200 relative flex h-[44px] items-center border-b px-3.5">
            {/* Browser dots */}
            <img
              alt="Browser window controls"
              src="/images/homepage/browser-dots.svg"
              className="mr-4 hidden h-3 w-[52px] shrink-0 md:block"
            />
            <div role="tablist" className="flex h-full items-end gap-0">
              {tabs.map((tab, i) => {
                const isActive = tab.id === activeTab;
                const nextIsActive = tabs[i + 1]?.id === activeTab;
                const showSeparator = i < tabs.length - 1 && !isActive && !nextIsActive;
                return (
                  <Fragment key={tab.id}>
                    <button
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActiveTab(tab.id)}
                      className={`relative flex items-center gap-1.5 text-xs font-medium transition-colors ${
                        isActive
                          ? "z-10 mb-[-1px] rounded-t-lg border border-b-white border-l-[#e4e4e7] border-r-[#e4e4e7] border-t-[#e4e4e7] bg-white px-3 pb-[9px] pt-2 text-[blue] before:absolute before:-bottom-px before:-left-2 before:size-2 before:bg-[radial-gradient(circle_at_0_0,transparent_6px,#e4e4e7_6px,#e4e4e7_7px,white_7px)] before:content-[''] after:absolute after:-bottom-px after:-right-2 after:size-2 after:bg-[radial-gradient(circle_at_100%_0,transparent_6px,#e4e4e7_6px,#e4e4e7_7px,white_7px)] after:content-['']"
                          : "text-ds-neutral-900/40 px-3 py-3"
                      }`}
                    >
                      <img alt={tab.label} src={tab.icon} className={`size-4 ${isActive ? "" : "opacity-40"}`} />
                      <span>{tab.label}</span>
                    </button>
                    {showSeparator && <span className="bg-ds-neutral-200 mb-3 h-5 w-px shrink-0" />}
                  </Fragment>
                );
              })}
            </div>
            {/* Plus button */}
            <button
              className="rounded-ds-7 border-ds-neutral-200 ml-auto hidden size-[29px] shrink-0 items-center justify-center border bg-white md:flex"
              aria-label="Add tab"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M6 1v10M1 6h10" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {/* Content area */}
          <div ref={contentRef} className="relative flex flex-col items-center overflow-clip">
            <div className="pointer-events-none absolute inset-0 z-0" aria-hidden="true">
              {sceneVisible && !sceneError ? (
                <UnicornScene
                  projectId="RwAccR4YsBdSpSTlO545"
                  width="1440px"
                  height="900px"
                  scale={1}
                  dpi={1.5}
                  fps={30}
                  production
                  className="h-full w-full"
                  onError={(e: unknown) => {
                    console.error("[AutomateOperations] Unicorn scene failed", e);
                    setSceneError(true);
                  }}
                />
              ) : (
                <img
                  alt="Workflow automation background"
                  src="/images/homepage/automate-bg.png"
                  className="w-full object-cover"
                />
              )}
            </div>
            {/* Header section */}
            <div className="relative z-[1] flex w-full flex-col items-start px-8 py-8 lg:py-16">
              <div className="flex w-full flex-col gap-8 md:flex-row md:flex-wrap md:items-start md:justify-between">
                <AnimatePresence mode="popLayout">
                  <motion.div
                    key={currentTab.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.35, ease: EASE_OUT_QUART }}
                    className="flex max-w-[768px] flex-1 flex-col gap-4"
                  >
                    <div className="flex flex-col gap-1.5">
                      <p className="text-ds-brand-500 text-base font-semibold">{currentTab.tagLabel}</p>
                      <h2 className="text-ds-text-subtle leading-label text-3xl font-medium tracking-[-0.96px] md:text-5xl">
                        {currentTab.headline}
                      </h2>
                    </div>
                    <p className="text-ds-text-subtle leading-label text-xl">{currentTab.subtitle}</p>
                  </motion.div>
                </AnimatePresence>
                <Link
                  href="/templates"
                  className="from-ds-neutral-100 to-ds-neutral-200 text-ds-text-primary shadow-ds-white relative flex h-11 items-center justify-center rounded-xl bg-gradient-to-b px-6 text-base font-medium transition-all hover:scale-[1.02]"
                >
                  Explore Template
                  <span className="rounded-inherit shadow-ds-inner-light pointer-events-none absolute inset-0" />
                </Link>
              </div>
            </div>

            {/* Badge pills */}
            <div className="scrollbar-hide relative z-[1] flex w-full gap-2 overflow-x-auto px-8 pb-6 md:flex-wrap md:px-16">
              <AnimatePresence mode="popLayout">
                <motion.div
                  key={currentTab.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="flex gap-2 md:flex-wrap"
                >
                  {currentTab.badges.map((badge, idx) => (
                    <motion.span
                      key={badge}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: idx * 0.05, ease: EASE_OUT_QUART }}
                      className="border-ds-neutral-200 bg-ds-surface-primary text-ds-text-subtle flex shrink-0 items-center gap-1 rounded-full border px-3 py-1 text-sm font-medium"
                    >
                      {badge}
                    </motion.span>
                  ))}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Video */}
            <div className="relative z-[1] w-full px-4 pb-4 lg:px-16 lg:pb-16">
              <div className="rounded-ds-9 border-ds-neutral-200 bg-ds-neutral-100 aspect-video overflow-hidden border">
                <video
                  key={currentTab.id}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="h-full w-full object-cover"
                  poster="/images/homepage/video-poster.jpg"
                >
                  <source src={currentTab.video.src} type={currentTab.video.type} />
                  Your browser does not support video playback.
                </video>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
